import type { ShopperProducts } from '@/scapi';
import { getMenuExternalUrl } from '@/lib/menu-link';

type Category = ShopperProducts.schemas['Category'];

export function getHomeBoxClickAnalyticsProps(
    category: Category,
    clickElement: 'button' | 'link'
): Record<'data-analytics-event' | 'data-analytics-payload', string> {
    return {
        'data-analytics-event': 'home_box_click',
        'data-analytics-payload': JSON.stringify({
            click_element: clickElement,
            click_text: category.name ?? '',
        }),
    };
}

export function isCategoryVisibleInMenu(category: Category): boolean {
    return Boolean(category.c_showInMenu);
}

export function isCategoryInEvidence(category: Category): boolean {
    return Boolean(category.c_inEvidence);
}

/**
 * Recursively keeps only categories flagged with `c_showInMenu`.
 * When `productCountById` is provided, empty L2+ categories are dropped (`productCount === 0`).
 * External menu links are kept even when they have no products.
 * Missing ids fail open.
 */
export function filterCategoriesForMenu(categories: Category[], productCountById?: Map<string, number>): Category[] {
    return filterCategoriesForMenuRecursive(categories, productCountById, false);
}

function filterCategoriesForMenuRecursive(
    categories: Category[],
    productCountById: Map<string, number> | undefined,
    pruneEmptyCategories: boolean
): Category[] {
    return categories.flatMap((category) => {
        if (!isCategoryVisibleInMenu(category)) {
            return [];
        }
        if (
            pruneEmptyCategories &&
            !getMenuExternalUrl(category.c_externalUrl) &&
            productCountById?.get(category.id) === 0
        ) {
            return [];
        }

        const children = category.categories
            ? filterCategoriesForMenuRecursive(category.categories, productCountById, true)
            : undefined;

        return [{ ...category, categories: children }];
    });
}
